import React from 'react';
import GlowingBackground from '../components/GlowingBackground';
import PopularCommands from '../components/PopularCommands';
import ProTips from '../components/ProTips';
import { Download, Shield, Lock, Cpu, Github, CheckCircle, Zap, Star, Users, Clock, Activity, Play } from 'lucide-react';

const Home = () => {
  const v3Features = [
    {
      icon: Zap,
      title: '34 Built-in Automations',
      description: 'Browser control, file management, Spotify integration, system tweaks (screenshots, volume), window arrangement, clipboard operations',
      badge: 'NEW'
    },
    {
      icon: Clock,
      title: 'Real-Time WebRTC Streaming',
      description: '300-500ms latency (6-9x faster than competitors). Ultra-low latency voice processing for instant command execution',
      badge: 'V3.0'
    },
    {
      icon: Activity,
      title: 'gRPC Bidirectional Processing',
      description: 'Efficient real-time communication between client and server for seamless voice-to-action pipeline',
      badge: 'V3.0'
    },
    {
      icon: Cpu,
      title: 'Microservices Architecture',
      description: 'Scalable, maintainable infrastructure designed for enterprise-level reliability and performance',
      badge: 'NEW'
    },
    {
      icon: Shield,
      title: 'Usage Dashboard',
      description: 'Real-time token and time tracking. Monitor your usage, manage limits, and optimize your workflow',
      badge: 'V3.0'
    },
    {
      icon: Lock,
      title: 'Enterprise Security',
      description: 'DTLS encryption, Row-Level Security (RLS), short-lived JWTs. End-to-end encrypted voice streaming',
      badge: 'NEW'
    }
  ];

  const testimonials = [
    {
      name: 'Alex Rivera',
      role: 'Senior Developer, Silicon Valley',
      text: 'Saved my workflow. The 300ms latency is game-changing. I automate my entire dev setup in seconds.',
      rating: 5
    },
    {
      name: 'Sarah Chen',
      role: 'Content Creator, Los Angeles',
      text: 'As someone with RSI, Twe is a lifesaver. Voice control without compromising speed or privacy.',
      rating: 5
    },
    {
      name: 'Marcus Thompson',
      role: 'Founder, Tech Startup',
      text: '2 hours saved daily. The multi-app automation is exactly what power users need.',
      rating: 5
    }
  ];

  const problemSolutions = [
    {
      problem: 'Repetitive Dev Tasks',
      solution: '"Dev Start" – Open VS Code, Spotify, GitHub tabs',
      savings: 'Saves 5 mins/session',
      persona: 'For Developers'
    },
    {
      problem: 'Context Switching Fatigue',
      solution: '"Research Mode" – Open 5 tabs and get AI summary',
      savings: 'Saves 10 mins on research',
      persona: 'For Power Users'
    },
    {
      problem: 'Physical Strain from Repetition',
      solution: 'Voice control eliminates clicking and typing',
      savings: 'Reduces RSI risk by 80%',
      persona: 'For Disabled Users'
    },
    {
      problem: 'Morning Routine Friction',
      solution: '"Morning Briefing" – Email, Calendar, Weather in one command',
      savings: 'Saves 3 mins daily',
      persona: 'For Creators'
    }
  ];

  const videoLinks = [
    { title: 'Low-Latency Demo: 300ms Response Time', url: 'https://www.youtube.com/embed/HLrtZxldXbQ' },
    { title: 'Dev Workflow Automation', url: 'https://www.youtube.com/embed/HLrtZxldXbQ' },
    { title: 'Voice-Controlled Browser Navigation', url: 'https://www.youtube.com/embed/-AHyMhOEoUE' },
    { title: 'Multi-App Orchestration', url: 'https://www.youtube.com/embed/2kcP0xJj9zw' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black">
      {/* Hero Section - Professional UX Layout */}
      <section className="relative pt-24 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[90vh] flex items-center">
        <GlowingBackground />
        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Text Content */}
            <div className="text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Control Your Apps
                <br />
                With The{' '}
                <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                  Power
                </span>
                <br />
                of{' '}
                <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                  Your Voice
                </span>
              </h1>
              
              <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-xl">
                Automate your browser and apps with fast, secure, and{' '}
                <strong className="text-white">private</strong> voice commands.
              </p>

              {/* Key Benefits */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span className="text-orange-400 font-medium">No Cloud Processing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span className="text-orange-400 font-medium">Instant 300ms Execution</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span className="text-orange-400 font-medium">Total Privacy</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
                <a
                  href="https://downloads.twe-assistant.com/latest/twe-voice-assistant-setup.exe"
                  download
                  data-testid="hero-download-btn"
                  className="group flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105"
                >
                  <Download className="w-5 h-5 group-hover:animate-bounce" />
                  <span>Download Twe</span>
                </a>
                <a
                  href="#see-twe-in-action"
                  className="flex items-center space-x-2 px-8 py-4 bg-transparent text-white font-semibold rounded-lg border border-white/20 hover:bg-white/5 transition-all duration-300"
                >
                  <Play className="w-5 h-5" />
                  <span>Watch Demo</span>
                </a>
              </div>

              {/* Supporting Text */}
              <div className="space-y-2">
                <p className="text-gray-400 text-sm">Free & Trusted by 10,000+ Power Users</p>
                <p className="text-gray-500 text-sm">Built for developers, creators, and disabled innovators.</p>
              </div>
            </div>

            {/* Right Side - App Mockup */}
            <div className="relative lg:pl-8">
              <div className="relative">
                {/* Glow Effect Behind Mockup */}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-500/20 blur-3xl rounded-full transform scale-110"></div>
                
                {/* App Mockup Card */}
                <div className="relative bg-zinc-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl">
                  {/* App Header */}
                  <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-white/10">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm font-bold">T</span>
                    </div>
                    <span className="text-white font-semibold">Twe</span>
                    <div className="flex-1"></div>
                    <div className="px-3 py-1 bg-zinc-800 rounded-lg text-gray-400 text-sm">
                      ⌘ Search
                    </div>
                  </div>

                  {/* Command List */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3 px-3 py-2 bg-zinc-800/50 rounded-lg">
                      <span className="text-blue-400">📁</span>
                      <span className="text-gray-300 text-sm">Open VS Code</span>
                    </div>
                    <div className="flex items-center space-x-3 px-3 py-2 bg-zinc-800/50 rounded-lg">
                      <span className="text-green-400">▶</span>
                      <span className="text-gray-300 text-sm">Start dev server</span>
                    </div>
                    <div className="flex items-center space-x-3 px-3 py-2 bg-zinc-800/50 rounded-lg">
                      <span className="text-green-500">🎵</span>
                      <span className="text-gray-300 text-sm">Launch Spotify</span>
                    </div>
                  </div>

                  {/* Voice Command Input */}
                  <div className="relative mb-6">
                    <div className="bg-zinc-800 rounded-xl p-4 border border-orange-500/30">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                        <span className="text-white">Start my dev environment...</span>
                      </div>
                      {/* Sound Wave Visualization */}
                      <div className="flex items-center justify-center space-x-1 mt-3">
                        {[...Array(20)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-gradient-to-t from-orange-600 to-orange-400 rounded-full animate-pulse"
                            style={{
                              height: `${Math.random() * 20 + 8}px`,
                              animationDelay: `${i * 0.05}s`
                            }}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Output Section */}
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Zap className="w-4 h-4 text-orange-500" />
                      <span className="text-gray-400 text-sm font-medium">Output</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-400">▶</span>
                        <span className="text-gray-300 text-sm">Starting dev environment...</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-gray-300 text-sm">Opened VS Code & started dev server</span>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Trust Section */}
      <section className="py-4 px-4 sm:px-6 lg:px-8 bg-zinc-950/50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-orange-500/20 rounded-2xl p-8 md:p-12">
            <div className="flex items-start space-x-4">
              <Shield className="w-12 h-12 text-orange-500 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Why can't I install?
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  Apps without a code-signing certificate are flagged by Microsoft Defender SmartScreen. We've purchased an EV Certificate and are in the process of setting it up. Until then, Windows may say "We don't know this app yet" even though it's safe.
                </p>
                <a 
                  href="/installation"
                  className="inline-flex items-center text-orange-500 hover:text-orange-400 font-medium transition-colors duration-200"
                >
                  Follow the Windows install guide.
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* V3.0 Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              V3.0: Built for Enterprise, Priced for Everyone
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Real features that solve real problems for power users, developers, creators, and anyone who values their time
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {v3Features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-zinc-900/50 backdrop-blur-sm border border-white/5 rounded-xl p-8 hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-7 h-7 text-orange-500" />
                    </div>
                    {feature.badge && (
                      <span className="px-3 py-1 bg-orange-500/20 text-orange-500 text-xs font-semibold rounded-full border border-orange-500/30">
                        {feature.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Video Demos Section */}
          <div id="see-twe-in-action" className="mb-16 scroll-mt-24">
            <h3 className="text-3xl font-bold text-white mb-8 text-center">
              See Twe in Action
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              {videoLinks.map((video, idx) => (
                <div key={idx} className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-orange-500/30 transition-all duration-300">
                  <div className="aspect-video bg-black">
                    <iframe
                      width="100%"
                      height="100%"
                      src={video.url}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                  <div className="p-4">
                    <h4 className="text-white font-semibold">{video.title}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problem-Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Real Problems. Real Solutions.
            </h2>
            <p className="text-xl text-gray-400">
              Quantifiable time savings for power users who value efficiency
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {problemSolutions.map((item, idx) => (
              <div key={idx} className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 hover:border-orange-500/30 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-red-500 text-2xl">⚠️</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-400 mb-2">Problem: {item.problem}</h3>
                    <div className="flex items-start space-x-4 mb-3">
                      <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-semibold mb-1">Solution: {item.solution}</h4>
                        <p className="text-green-400 text-sm font-semibold">{item.savings}</p>
                      </div>
                    </div>
                    <span className="inline-block px-3 py-1 bg-orange-500/10 text-orange-500 text-xs font-semibold rounded-full border border-orange-500/20">
                      {item.persona}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-2xl font-bold text-orange-500 mb-2">
              Users report saving 2+ hours daily
            </p>
            <p className="text-gray-400">
              That's 730 hours per year. What would you do with an extra month?
            </p>
          </div>
        </div>
      </section>

      {/* Popular Commands Section */}
      <PopularCommands />

      {/* Pro Tips Section */}
      <ProTips />

      {/* Trust Signals & Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Trusted by 10,000+ Power Users
            </h2>
            <p className="text-xl text-gray-400">
              Join developers, creators, and professionals who've reclaimed their time
            </p>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-8 mb-16">
            <div className="flex items-center space-x-2 px-6 py-3 bg-zinc-900/50 border border-white/5 rounded-lg">
              <Shield className="w-6 h-6 text-green-500" />
              <span className="text-gray-300 font-semibold">TLS 1.3 Encrypted</span>
            </div>
            <div className="flex items-center space-x-2 px-6 py-3 bg-zinc-900/50 border border-white/5 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <span className="text-gray-300 font-semibold">OpenAI Compliant</span>
            </div>
            <div className="flex items-center space-x-2 px-6 py-3 bg-zinc-900/50 border border-white/5 rounded-lg">
              <Github className="w-6 h-6 text-orange-500" />
              <span className="text-gray-300 font-semibold">Open Source Core</span>
            </div>
            <div className="flex items-center space-x-2 px-6 py-3 bg-zinc-900/50 border border-white/5 rounded-lg">
              <Lock className="w-6 h-6 text-green-500" />
              <span className="text-gray-300 font-semibold">No Data Tracking</span>
            </div>
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((test, idx) => (
              <div key={idx} className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 hover:border-orange-500/30 transition-all duration-300">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(test.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-orange-500 fill-orange-500" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed italic">
                  "{test.text}"
                </p>
                <div>
                  <p className="text-white font-semibold">{test.name}</p>
                  <p className="text-gray-400 text-sm">{test.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-zinc-900 to-black border border-orange-500/30 rounded-2xl p-12">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Automate Everything?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join 10,000+ power users, developers, and creators who've eliminated workflow friction. 
              Free tier available. 30-day money-back guarantee.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://downloads.twe-assistant.com/latest/twe-voice-assistant-setup.exe"
                download
                className="inline-flex items-center space-x-2 px-10 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105"
              >
                <Download className="w-5 h-5" />
                <span>Download Twe V3.0 Free</span>
              </a>
              <a
                href="https://discord.gg/AUezcNccMd"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-8 py-4 bg-zinc-800 text-white font-semibold rounded-lg hover:bg-zinc-700 transition-all duration-300 border border-white/10"
              >
                <Users className="w-5 h-5" />
                <span>Join Community</span>
              </a>
            </div>
            <p className="text-gray-400 mt-6 text-sm">
              Windows 10/11 • No credit card required • Secure & Private
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
